// src/components/mediaKit/MediaKitRenderer.jsx
import React, { useState } from 'react';
import {
    MapPin, Mail, Copy, Check, ShieldCheck, Sparkles, Play, X, ExternalLink,
} from 'lucide-react';
import PlatformIcon from './PlatformIcon';
import AmazonHlsPlayer from './AmazonHlsPlayer';
import { TikTokEmbed, InstagramEmbed } from './SocialEmbed';

const PLATFORM_DOT = {
    amazon: 'from-orange-400 to-orange-600',
    youtube: 'from-red-500 to-red-700',
    tiktok: 'from-cyan-400 to-pink-500',
    instagram: 'from-pink-500 via-fuchsia-500 to-amber-400',
    custom: 'from-slate-400 to-slate-600',
};

function VideoLightbox({ video, onClose }) {
    if (!video) return null;

    const renderPlayer = () => {
        if (video.platform === 'youtube' && video.embed_url) {
            return (
                <iframe
                    src={video.embed_url}
                    className="w-full aspect-video"
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                />
            );
        }
        if (video.platform === 'amazon' && video.embed_url) {
            return <AmazonHlsPlayer src={video.embed_url} />;
        }
        if (video.platform === 'tiktok') {
            return <div className="bg-white p-4"><TikTokEmbed url={video.source_url} /></div>;
        }
        if (video.platform === 'instagram') {
            return <div className="bg-white p-4"><InstagramEmbed url={video.source_url} /></div>;
        }
        return (
            <div className="aspect-video flex flex-col items-center justify-center gap-4 text-white p-8 text-center">
                <p className="text-sm text-slate-300">This platform doesn't allow inline playback here.</p>
                <a href={video.source_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black font-semibold text-sm">
                    Watch on {video.platform} <ExternalLink size={14} />
                </a>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
            <div className="relative w-full max-w-3xl bg-black rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center">
                    <X size={18} />
                </button>
                {renderPlayer()}
            </div>
        </div>
    );
}

function VideoCard({ video, aspect = 'video' }) {
    const [broken, setBroken] = useState(false);
    return (
        <div className="group rounded-xl overflow-hidden border border-black/5 bg-black/5 cursor-pointer relative">
            <div className={`relative ${aspect === 'vertical' ? 'aspect-[9/16]' : 'aspect-video'} bg-slate-800`}>
                {video.thumbnail_url && !broken ? (
                    <img src={video.thumbnail_url} alt={video.title || ''} onError={() => setBroken(true)}
                        className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">No preview</div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition">
                    <div className="w-11 h-11 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow">
                        <Play size={18} className="text-black ml-0.5" fill="currentColor" />
                    </div>
                </div>
                <span className={`absolute top-2 left-2 text-[10px] font-bold uppercase text-white px-2 py-0.5 rounded-full bg-gradient-to-r ${PLATFORM_DOT[video.platform] || PLATFORM_DOT.custom}`}>
                    {video.platform}
                </span>
            </div>
            {video.title && (
                <div className="px-3 py-2 bg-white">
                    <p className="text-xs font-semibold text-slate-800 line-clamp-2">{video.title}</p>
                </div>
            )}
        </div>
    );
}

function CopyEmail({ email }) {
    const [copied, setCopied] = useState(false);
    if (!email) return null;
    return (
        <button
            onClick={() => { navigator.clipboard.writeText(email); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            className="inline-flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100"
        >
            <Mail size={14} /> {email} {copied ? <Check size={13} /> : <Copy size={13} />}
        </button>
    );
}

// ── shared section pieces ─────────────────────────────────────

function StatGrid({ stats, tone }) {
    const filtered = (stats || []).filter((s) => s.value !== undefined && s.value !== null && String(s.value).trim() !== '');
    if (!filtered.length) return null;
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {filtered.map((s, i) => (
                <div key={i} className={tone.statCard}>
                    <p className={tone.statLabel}>{s.label}</p>
                    <p className={tone.statValue} style={tone.statValueStyle}>{s.value}</p>
                    {s.verified && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold mt-1 opacity-70">
                            <ShieldCheck size={12} /> Verified
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}

function RateCard({ items, tone }) {
    if (!items?.length) return null;
    return (
        <div className={tone.panel}>
            <h2 className={tone.sectionTitle}>Rates</h2>
            <div className="divide-y divide-black/5">
                {items.map((r, i) => (
                    <div key={i} className="py-4 grid grid-cols-1 sm:grid-cols-[7fr_3fr] gap-x-6 gap-y-2">
                        <div>
                            <p className="font-semibold text-sm">{r.title}</p>
                            {r.note && <p className="text-xs opacity-60 mt-1 leading-relaxed">{r.note}</p>}
                        </div>
                        <div className="sm:text-right">
                            <p className="font-bold text-base">{r.price}</p>
                            {r.price_note && <p className="text-[11px] opacity-60 mt-1 leading-relaxed">{r.price_note}</p>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function SpecChips({ specializations, tone }) {
    const { video_types = [], categories = [] } = specializations || {};
    if (!video_types.length && !categories.length) return null;
    return (
        <div className={tone.panel}>
            <h2 className={tone.sectionTitle}>Specializations</h2>
            {video_types.length > 0 && (
                <div className="mb-3">
                    <p className="text-xs font-semibold opacity-60 mb-1.5">Video types</p>
                    <div className="flex flex-wrap gap-2">
                        {video_types.map((v, i) => <span key={i} className={tone.chip}>{v}</span>)}
                    </div>
                </div>
            )}
            {categories.length > 0 && (
                <div>
                    <p className="text-xs font-semibold opacity-60 mb-1.5">Categories</p>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((c, i) => <span key={i} className={tone.chip}>{c}</span>)}
                    </div>
                </div>
            )}
        </div>
    );
}

function SocialsRow({ socials, tone }) {
    if (!socials?.length) return null;
    return (
        <div className="flex flex-wrap gap-2">
            {socials.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noreferrer" className={tone.socialPill}>
                    <PlatformIcon platform={s.platform} size={26} />
                    <span className="flex flex-col leading-tight">
                        <span className="text-[9px] uppercase tracking-wider opacity-70 font-bold">{s.platform}</span>
                        <span className="text-xs font-bold">{s.label || s.sub_label || s.url}</span>
                    </span>
                </a>
            ))}
        </div>
    );
}

function VideoGallery({ videos, tone }) {
    const [active, setActive] = useState(null);
    const grouped = (videos || []).reduce((acc, v) => {
        (acc[v.section_label] = acc[v.section_label] || []).push(v);
        return acc;
    }, {});
    if (!videos?.length) return null;
    return (
        <div className="space-y-8">
            {Object.entries(grouped).map(([label, vids]) => (
                <div key={label}>
                    <h3 className={tone.gallerySectionTitle}>{label}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {vids.map((v) => (
                            <div key={v.id} onClick={() => setActive(v)}>
                                <VideoCard video={v} aspect={v.platform === 'tiktok' || v.platform === 'instagram' ? 'vertical' : 'video'} />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
            <VideoLightbox video={active} onClose={() => setActive(null)} />
        </div>
    );
}

// ── layout tone presets (this is where "3 different backgrounds/layouts" lives) ──

function toneFor(layout, theme) {
    const primary = theme?.primary || '#7c3aed';
    const accent = theme?.accent || '#06b6d4';

    if (layout === 'paperlight') {
        return {
            page: 'bg-slate-50 text-slate-900',
            hero: 'bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8',
            panel: 'bg-white rounded-2xl shadow-sm border border-slate-200 p-6',
            sectionTitle: 'text-lg font-bold mb-4',
            gallerySectionTitle: 'text-base font-bold mb-3',
            statCard: 'bg-slate-50 border border-slate-200 rounded-xl p-4',
            statLabel: 'text-[11px] uppercase font-semibold text-slate-500',
            statValue: 'text-2xl font-black mt-1',
            chip: 'text-xs font-medium px-3 py-1 rounded-full bg-slate-100 border border-slate-200',
            socialPill: 'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white pl-1.5 pr-3.5 py-1.5 hover:border-slate-300 transition',
            accentText: { color: primary },
        };
    }

    if (layout === 'editorial') {
        return {
            page: 'bg-white text-slate-900',
            hero: 'border-b-4 pb-8',
            heroStyle: { borderColor: primary },
            panel: 'py-6 border-b border-slate-200',
            sectionTitle: 'text-sm font-black uppercase tracking-[0.25em] mb-4',
            gallerySectionTitle: 'text-xs font-black uppercase tracking-[0.2em] mb-3',
            statCard: 'p-0 border-r last:border-r-0 border-slate-200 pr-4',
            statLabel: 'text-[11px] uppercase tracking-wide text-slate-500',
            statValue: 'text-3xl font-black mt-1',
            chip: 'text-xs font-bold uppercase tracking-wide px-3 py-1 border border-slate-900',
            socialPill: 'inline-flex items-center gap-2 pl-0 pr-3 py-1 hover:opacity-70 transition',
            accentText: { color: primary },
        };
    }

    // default: 'aurora' — dark, glowing, gradient-blob background
    return {
        page: 'text-slate-100',
        pageStyle: { background: `radial-gradient(circle at 15% 0%, ${primary}33, transparent 45%), radial-gradient(circle at 85% 15%, ${accent}33, transparent 45%), #0b0f19` },
        hero: 'rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 sm:p-8 shadow-2xl',
        panel: 'rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 shadow-xl',
        sectionTitle: 'text-xs font-black uppercase tracking-[0.3em] mb-4',
        gallerySectionTitle: 'text-sm font-black tracking-tight mb-3',
        statCard: 'rounded-xl border border-white/10 bg-white/5 p-4',
        statLabel: 'text-[10px] uppercase tracking-wider text-slate-400 font-semibold',
        statValue: `mt-1 text-2xl font-black bg-clip-text text-transparent`,
        statValueStyle: { backgroundImage: `linear-gradient(90deg, ${primary}, ${accent})` },
        chip: 'text-xs font-semibold px-3 py-1 rounded-full bg-white/10 border border-white/10',
        socialPill: 'inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 pl-1.5 pr-3.5 py-1.5 hover:bg-white/10 transition',
        accentText: { color: accent },
    };
}

export default function MediaKitRenderer({ kit, videos, isPreview = false }) {
    if (!kit) return null;
    const tone = toneFor(kit.layout, kit.theme);
    const radius = { lg: '0.5rem', xl: '0.875rem', '2xl': '1.25rem' }[kit.theme?.radius] || '0.875rem';
    const fontFamily = { serif: 'Georgia, serif', mono: 'monospace' }[kit.theme?.font] || 'inherit';

    return (
        <div className={`min-h-screen w-full ${tone.page}`} style={{ ...tone.pageStyle, fontFamily, '--mk-radius': radius }}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">

                {/* Hero */}
                <section className={tone.hero} style={tone.heroStyle}>
                    <div className="flex flex-col sm:flex-row gap-6 items-start">
                        {kit.avatar_url && (
                            <img src={kit.avatar_url} alt={kit.display_name}
                                className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{kit.display_name}</h1>
                            {kit.tagline && <p className="text-sm opacity-70 mt-1">{kit.tagline}</p>}

                            {(kit.badges || []).length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {kit.badges.map((b, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full" style={{ ...tone.accentText, background: 'rgba(125,125,125,0.12)' }}>
                                            <Sparkles size={12} /> {b.replace('_', ' ')}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {kit.bio && <p className="mt-3 text-sm leading-relaxed opacity-90 max-w-2xl">{kit.bio}</p>}

                            <div className="mt-3 flex flex-wrap items-center gap-4">
                                {kit.location && (
                                    <span className="inline-flex items-center gap-1 text-xs opacity-70"><MapPin size={13} /> {kit.location}</span>
                                )}
                                {kit.show_contact_button && <CopyEmail email={kit.contact_email} />}
                            </div>

                            <div className="mt-4"><SocialsRow socials={kit.socials} tone={tone} /></div>
                        </div>
                    </div>
                </section>

                <StatGrid stats={kit.stats} tone={tone} />
                <SpecChips specializations={kit.specializations} tone={tone} />
                <RateCard items={kit.rate_card} tone={tone} />

                {videos?.length > 0 && (
                    <section>
                        <h2 className={tone.sectionTitle}>Selected work</h2>
                        <VideoGallery videos={videos} tone={tone} />
                    </section>
                )}

                {!isPreview && (
                    <footer className="text-center text-[11px] uppercase tracking-[0.3em] opacity-40 pt-6">
                        Media kit by Multi Influencer Pro
                    </footer>
                )}
            </div>
        </div>
    );
}